import { Pipe, PipeTransform } from '@angular/core';

/** Milliseconds in one day. */
const MS_PER_DAY = 86_400_000;
/** Milliseconds in one hour. */
const MS_PER_HOUR = 3_600_000;
/** Milliseconds in one minute. */
const MS_PER_MINUTE = 60_000;
/** Milliseconds in one second. */
const MS_PER_SECOND = 1_000;

/**
 * Formats how long ago a dashboard item was requested, based on the
 * elapsed time since its created timestamp.
 *
 * Output tiers (largest applicable unit wins):
 * - `>= 1` whole day  → `"Requested X day(s) ago"`
 * - `>= 1` hour       → `"Requested X hour(s) ago"`
 * - `>= 1` minute     → `"Requested X minute(s) ago"`
 * - otherwise         → `"Requested X second(s) ago"` (clamped to a minimum of 1)
 *
 * Future or unparseable dates fall back to `"Recently"`.
 *
 * @example
 * {{ item.requested_date | wcgRequestedMessage }}
 * <!-- "Requested 3 hours ago" -->
 */
@Pipe({
  name: 'wcgRequestedMessage',
})
export class RequestedMessagePipe implements PipeTransform {
  /**
   * Transforms an ISO date string into a relative "Requested … ago" message.
   *
   * @param requestedDate - ISO datetime string (e.g. `2026-07-28T09:00:00.000000`)
   *   or date-only string (e.g. `2026-07-28`), interpreted in local time.
   * @returns Relative message such as `"Requested 2 days ago"` or
   *   `"Requested 5 minutes ago"`; `"Recently"` for future or invalid dates.
   */
  transform(requestedDate: string): string {
    // Date-only strings anchor to local midnight so a UTC parse can't shift the day
    const requested = requestedDate.includes('T')
      ? new Date(requestedDate)
      : new Date(`${requestedDate}T00:00:00`);

    const diffMs = Date.now() - requested.getTime();
    if (Number.isNaN(requested.getTime()) || diffMs < 0) return 'Recently';

    const days = Math.floor(diffMs / MS_PER_DAY);
    if (days >= 1) return this._message(days, 'day');

    const hours = Math.floor(diffMs / MS_PER_HOUR);
    if (hours >= 1) return this._message(hours, 'hour');

    const minutes = Math.floor(diffMs / MS_PER_MINUTE);
    if (minutes >= 1) return this._message(minutes, 'minute');

    return this._message(
      Math.max(Math.floor(diffMs / MS_PER_SECOND), 1),
      'second',
    );
  }

  /**
   * Builds the pluralized "Requested … ago" message for a unit value.
   *
   * @param value - Whole number of elapsed units (>= 1).
   * @param unit - Singular unit name (`day`, `hour`, `minute`, `second`).
   * @returns Message such as `"Requested 1 hour ago"` or `"Requested 3 hours ago"`.
   */
  private _message(value: number, unit: string): string {
    return `Requested ${value} ${unit}${value === 1 ? '' : 's'} ago`;
  }
}
